/**
 * Dealer Price Block - Dynamic Updates
 * Updates dealer price when product variant changes or addons are selected
 */

class DealerPriceBlock {
  constructor() {
    this.basePrice = 0;
    this.dealerDiscount = 20;
    this.init();
  }

  init() {
    // Listen for variant changes
    document.addEventListener('DOMContentLoaded', () => {
      this.setupVariantListeners();
      this.setupAddonListeners();
      this.initializePrices();
    });
  }

  initializePrices() {
    const dealerPriceWrapper = document.querySelector('.dealer-price-wrapper');
    if (dealerPriceWrapper) {
      if (dealerPriceWrapper.dataset.dealerDiscount) {
        this.dealerDiscount = parseFloat(dealerPriceWrapper.dataset.dealerDiscount);
      }
      if (dealerPriceWrapper.dataset.retailPrice) {
        this.basePrice = parseInt(dealerPriceWrapper.dataset.retailPrice);
      }
      console.log('Dealer Price Block: Initialized with', {
        basePrice: this.formatMoney(this.basePrice),
        dealerDiscount: this.dealerDiscount + '%'
      });
    } else {
      console.warn('Dealer Price Block: .dealer-price-wrapper not found - block may not be on this page');
    }
  }

  setupAddonListeners() {
    // Listen for addon changes
    const addonDropdowns = document.querySelectorAll('[data-addon-select]');
    console.log(`Dealer Price Block: Found ${addonDropdowns.length} addon dropdown(s)`);
    
    addonDropdowns.forEach((dropdown, index) => {
      dropdown.addEventListener('change', () => {
        console.log(`Dealer Price Block: Addon dropdown ${index + 1} changed`);
        this.updateDealerPriceWithAddons();
      });
    });
    
    // Also listen for custom addon change events
    document.addEventListener('addon:changed', () => {
      console.log('Dealer Price Block: Received addon:changed event');
      this.updateDealerPriceWithAddons();
    });
  }

  setupVariantListeners() {
    // Find all product forms
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      // Listen for variant changes on the form
      form.addEventListener('change', (event) => {
        if (event.target.name === 'id') {
          this.updateDealerPrice(event.target.value);
        }
      });
    });

    // Also listen for custom variant change events (if theme uses them)
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateDealerPrice(event.detail.variant.id, event.detail.variant);
      }
    });
  }

  updateDealerPrice(variantId, variantData = null) {
    const dealerPriceElement = document.querySelector('.dealer-price-amount');
    
    if (!dealerPriceElement) return;

    // If variant data is provided, use it directly
    if (variantData && variantData.price) {
      this.basePrice = variantData.price;
      // Update base retail price data attribute so addon calc stays accurate
      const retailEl = document.querySelector('.dealer-price-retail');
      if (retailEl) retailEl.dataset.baseRetailPrice = variantData.price;
      const wrapper = document.querySelector('.dealer-price-wrapper');
      if (wrapper) wrapper.dataset.retailPrice = variantData.price;
      this.updatePriceDisplay(variantData.price, dealerPriceElement);
      return;
    }

    // Otherwise, fetch variant data
    if (variantId) {
      this.fetchVariantData(variantId).then(variant => {
        if (variant && variant.price) {
          this.basePrice = variant.price;
          const retailEl = document.querySelector('.dealer-price-retail');
          if (retailEl) retailEl.dataset.baseRetailPrice = variant.price;
          const wrapper = document.querySelector('.dealer-price-wrapper');
          if (wrapper) wrapper.dataset.retailPrice = variant.price;
          this.updatePriceDisplay(variant.price, dealerPriceElement);
        }
      });
    }
  }

  updateDealerPriceWithAddons() {
    console.log('Dealer Price Block: updateDealerPriceWithAddons called');
    
    const dealerPriceElement = document.querySelector('.dealer-price-amount');
    if (!dealerPriceElement) {
      console.warn('Dealer Price Block: No dealer-price-amount element found');
      return;
    }

    // Get base price from the dealer price wrapper if not already set
    const dealerPriceWrapper = document.querySelector('.dealer-price-wrapper');
    if (!this.basePrice && dealerPriceWrapper && dealerPriceWrapper.dataset.retailPrice) {
      this.basePrice = parseInt(dealerPriceWrapper.dataset.retailPrice);
      console.log('Dealer Price Block: Base price from wrapper:', this.basePrice);
    }
    
    // If still no base price, try to get from element data attribute
    if (!this.basePrice && dealerPriceElement.dataset.baseDealerPrice) {
      // Calculate back to retail price from dealer price
      const baseDealerPrice = parseInt(dealerPriceElement.dataset.baseDealerPrice);
      this.basePrice = Math.round(baseDealerPrice / ((100 - this.dealerDiscount) / 100));
      console.log('Dealer Price Block: Base price calculated from dealer price:', this.basePrice);
    }
    
    // Safety check
    if (!this.basePrice) {
      console.warn('Dealer Price Block: Unable to determine base price');
      return;
    }

    // Check if ProductAddons is available
    if (!window.productAddons) {
      console.warn('Dealer Price Block: window.productAddons not found');
      return;
    }
    
    if (typeof window.productAddons.getSelectedAddons !== 'function') {
      console.warn('Dealer Price Block: getSelectedAddons method not found');
      return;
    }

    // Get selected addons from ProductAddons instance
    let addonsTotal = 0;
    let addonsRetailTotal = 0;
    let hasAddons = false;
    
    const selectedAddons = window.productAddons.getSelectedAddons();
    console.log('Dealer Price Block: Selected addons:', selectedAddons);
    
    selectedAddons.forEach(addon => {
      if (addon.type === 'product' && addon.price) {
        let addonPrice = addon.price;
        
        // Track full retail value of addon (for strikethrough display)
        addonsRetailTotal += addonPrice;

        // Apply addon's specific dealer discount
        const addonDiscount = addon.dealerDiscount || this.dealerDiscount;
        if (addonDiscount > 0) {
          const discountMultiplier = (100 - addonDiscount) / 100;
          addonPrice = Math.round(addonPrice * discountMultiplier);
        }
        
        console.log(`Dealer Price Block: Addon "${addon.title}" price: ${addonPrice} (${addonDiscount}% discount)`);
        addonsTotal += addonPrice;
        hasAddons = true;
      }
    });

    // Calculate dealer price for base product
    const discountMultiplier = (100 - this.dealerDiscount) / 100;
    const baseDealerPrice = Math.round(this.basePrice * discountMultiplier);
    
    // Total dealer price including addons
    const totalDealerPrice = baseDealerPrice + addonsTotal;

    // Total retail price including addons (for strikethrough)
    const totalRetailPrice = this.basePrice + addonsRetailTotal;

    // Add visual update animation
    dealerPriceElement.classList.add('updating');
    setTimeout(() => dealerPriceElement.classList.remove('updating'), 300);

    // Update dealer price display
    const formattedDealerPrice = this.formatMoney(totalDealerPrice);
    dealerPriceElement.textContent = formattedDealerPrice;

    // Update strikethrough retail reference
    const retailPriceElement = document.querySelector('.dealer-price-retail');
    if (retailPriceElement) {
      retailPriceElement.textContent = this.formatMoney(totalRetailPrice);
    }

    // Update wrapper data attribute to show addons indicator
    if (dealerPriceWrapper) {
      dealerPriceWrapper.setAttribute('data-has-addons', hasAddons ? 'true' : 'false');
    }

    // Log for debugging
    console.log('Dealer Price Block Updated:', {
      base: this.formatMoney(baseDealerPrice),
      retail: this.formatMoney(totalRetailPrice),
      addons: this.formatMoney(addonsTotal),
      total: formattedDealerPrice,
      hasAddons: hasAddons
    });
  }

  async fetchVariantData(variantId) {
    try {
      // Get product handle from URL or data attribute
      const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) return null;

      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      
      // Find the specific variant
      const variant = productData.variants.find(v => v.id == variantId);
      return variant;
    } catch (error) {
      console.error('Error fetching variant data:', error);
      return null;
    }
  }

  updatePriceDisplay(retailPriceCents, dealerPriceElement) {
    // Store base price
    this.basePrice = retailPriceCents;
    
    // Get dealer discount percentage from data attribute or default to 20%
    const dealerPriceWrapper = document.querySelector('.dealer-price-wrapper');
    
    if (dealerPriceWrapper && dealerPriceWrapper.dataset.dealerDiscount) {
      this.dealerDiscount = parseFloat(dealerPriceWrapper.dataset.dealerDiscount);
    } else {
      // Try to extract from savings display
      const savingsElement = document.querySelector('.dealer-price-savings');
      if (savingsElement) {
        const match = savingsElement.textContent.match(/(\d+)%/);
        if (match) {
          this.dealerDiscount = parseInt(match[1]);
        }
      }
    }

    // Calculate dealer price
    const discountMultiplier = (100 - this.dealerDiscount) / 100;
    const dealerPriceCents = Math.round(retailPriceCents * discountMultiplier);

    // Update dealer price display
    if (dealerPriceElement) {
      dealerPriceElement.textContent = this.formatMoney(dealerPriceCents);
    }

    // Update strikethrough retail reference (base variant price, no addons yet)
    const retailPriceElement = document.querySelector('.dealer-price-retail');
    if (retailPriceElement) {
      retailPriceElement.textContent = this.formatMoney(retailPriceCents);
    }

    // Now factor in any selected addons (will override both prices if addons present)
    this.updateDealerPriceWithAddons();
  }

  formatMoney(cents) {
    // Get Shopify money format from theme settings
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
    
    // Convert cents to dollars
    const amount = (cents / 100).toFixed(2);
    
    // Replace placeholder with amount
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.dealerPriceBlock = new DealerPriceBlock();
    console.log('Dealer Price Block: Initialized on DOMContentLoaded');
  });
} else {
  window.dealerPriceBlock = new DealerPriceBlock();
  console.log('Dealer Price Block: Initialized immediately');
}

